// PRFAQSection.js - New Zealand
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Minus } from 'lucide-react';

const PRFAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: 'What points score do I need?',
      answer: 'You need 160 points to lodge an Expression of Interest under the Skilled Migrant Category, though selection from the pool usually happens at 180 points or above. Points come from age, where the 20-39 band earns 30, from qualifications, where a bachelor degree earns 40, a master\'s 50 and a PhD 70, from skilled employment in New Zealand at 50-60 points, and from work experience and partner qualifications.'
    },
    {
      question: 'Is a job offer necessary?',
      answer: 'Under the Skilled Migrant Category a New Zealand job offer adds 50-60 points and makes selection far more likely. Applying without one is technically possible, but most people who get through already have employment lined up. For Work to Residence the offer is not optional at all: it has to come from an accredited employer.'
    },
    {
      question: 'What English level is required?',
      answer: 'IELTS 6.5 overall with no individual band under 6.0, or an accepted equivalent, which means TOEFL iBT 79, PTE Academic 58 or OET Grade B. Your result has to be less than 2 years old at the time you apply. Scoring above the minimum earns no extra points; it simply satisfies the requirement.'
    },
    {
      question: 'How long will the process take?',
      answer: 'Skilled Migrant Category cases usually run 8-18 months from lodging the EOI to the residence grant. That total covers an unpredictable wait for selection, the 4 months you get to file after an invitation, and 6-12 months of processing. Work to Residence is much longer at 30-40 months, because the work visa period counts towards it.'
    },
    {
      question: 'Can my family be included?',
      answer: 'Yes. Your partner and any dependent children, meaning under 24, single and financially dependent, can go on the same residence application. A partner with qualifications or skilled employment can add up to 20 points to your score. Government charges rise for each additional person included.'
    },
    {
      question: 'What does accredited employer mean?',
      answer: 'It refers to a New Zealand employer that Immigration NZ has approved to hire migrant workers. Accreditation confirms the business is genuine, complies with employment law and follows sound employment practice. Work to Residence pathways only accept job offers from employers holding that status.'
    },
    {
      question: 'Which occupations are in demand?',
      answer: 'Shortages persist in healthcare, particularly nurses and doctors, in IT roles such as software developers and analysts, across civil, mechanical and electrical engineering, in construction trades including electricians, plumbers and carpenters, and in teaching. The Long Term Skill Shortage List (LTSSL) sets out the occupations where shortages have been sustained.'
    },
    {
      question: 'What can I do once residence is granted?',
      answer: 'You can live, work and study anywhere in New Zealand with no time limit on it, and you have access to public healthcare and education. Your resident visa lets you travel in and out of the country for up to 2 years. After 5 years of residence you may apply for citizenship, and since New Zealand permits dual citizenship you do not have to give up the one you hold.'
    },
    {
      question: 'Does age rule me out?',
      answer: 'For the Skilled Migrant Category you must be under 56 when you apply. The 20-39 band earns the full 30 points, 40-44 earns 20, 45-49 earns 10 and 50-55 earns 5. Work to Residence and Partnership categories carry no age limit at all.'
    },
    {
      question: 'How likely is an application to succeed?',
      answer: 'Applications that satisfy every requirement generally do well. Skilled Migrant Category cases carrying 180 points or more with complete documentation see approval rates in the region of 85-90%. We do not publish a success rate of our own. Eligibility, points and thorough paperwork are what make the difference.'
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            Common Questions
          </h2>
          <p className="text-gray-600 text-base font-light">
            The points that come up most often when people ask us about New Zealand residence
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="bg-surface rounded-lg shadow-sm overflow-hidden border border-gray-200"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
              >
                <span className="text-base font-semibold text-primary pr-4">
                  {faq.question}
                </span>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus size={24} className="text-secondary" />
                  ) : (
                    <Plus size={24} className="text-secondary" />
                  )}
                </div>
              </button>

              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-600 text-sm font-light leading-relaxed border-t border-gray-200 pt-4">
                      {faq.answer}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6 text-center"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Not covered here?</strong> Book a free consultation and we will talk through your circumstances and what they mean for a New Zealand residence application.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default PRFAQSection;
